import { alpha, Box, TextField, Typography, useMediaQuery } from "@mui/material";
import React, { useEffect, useState, useMemo } from "react";
import { useHistory } from "react-router-dom";
import CustomDialog from "./Dialog";
import { useDispatch, useSelector } from "react-redux";
import ReactGA from "react-ga4";
import { getAugmentedStyle } from "../../utils/augmentedStyles";
import { formatDate } from "../../utils/time";
import { displayNum, toFixed } from "../../utils/strings";
import OutlinedButton from "../Buttons/OutlinedButton";
import CustomButton from "../Buttons/Button";
import useBreakpoints from "../../hooks/useBreakpoints";
import { PATH_HISTORY } from "../../routes/paths";
import { currencies, getBalance } from "../../constants/currencies";
import {
  checkEmailValidation,
  transfer,
  setTransferSuccessDialogOpen
} from "../../redux/slices/DMA";
import { NOTIF_FAIL, setNotification } from "../../redux/slices/notifications";
import {
  setBindGoogle2FADialogOpen,
  setBindGoogle2FACallback,
  setCurrentFlow,
  setCurrentDialogStep
} from "../../redux/slices/account";
import { setAccountNotification } from "../../redux/slices/accountNotifications";
import ConfirmationDialog from "./ConfirmationDialog";
import Google2FAVerifyDialog from "./Google2FAVerifyDialog";
import BindConfirmDialog from "./BindConfirmDialog";
import { ReactComponent as LogoIcon } from "../../assets/icons/dashboard/token-branded_usdc.svg";
import { ReactComponent as SwapIcon } from "../../assets/icons/dashboard/transfer-swap.svg";
import { getUserInfo } from "../../redux/slices/auth";

function validateEmail(email) {
  const res =
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return res.test(String(email).toLowerCase());
}

function InfoRow({ title, Icon = null, info, rowSx }) {
  return (
    <Box
      sx={{
        display: "flex",
        mt: 4,
        flexFlow: "row nowrap",
        ...rowSx
      }}>
      <Typography sx={{ fontSize: 14, minWidth: 140, color: (theme) => theme.palette.grey[60] }}>
        {title}
      </Typography>

      <Typography
        sx={{ fontSize: 14, display: "flex", alignItems: "flex-end", wordBreak: "break-word" }}>
        {Icon && <Icon />} {info}
      </Typography>
    </Box>
  );
}

const InputTextSx = {
  color: (theme) => theme.palette.grey[0],
  // fontSize: { xxs: 18, xs: 24 },
  fontWeight: 400,
  py: "13.5px",
  px: "16px"
};

const RowContainerSx = {
  display: "flex",
  flexFlow: "row nowrap",
  alignItems: { xxs: "flex-start", xs: "center" }
};

const InputSx = {
  // height: { xxs: "50px", xs: "50px" },
  input: { ...InputTextSx },
  "& input[type=number]": {
    MozAppearance: "textfield",
    fontFamily: "Canva Sans Sans",
    letterSpacing: "1.28px",
    fontSize: "16px",
    paddingRight: "96px",
    fontWeight: "400"
  },
  "& input[type=number]::-webkit-outer-spin-button": {
    WebkitAppearance: "none",
    margin: 0
  },
  "& input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0
  },
  "& .MuiOutlinedInput-root": {
    "& fieldset": {
      border: "none"
    }
  }
};

function CustomInputField({
  value,
  setValue,
  placeholder,
  type = "number",
  helperText = "",
  maxValue,
  minValue = 0
}) {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  function handleChangeQuantity(value) {
    if (value.length === 0) setValue("");
    else {
      const floatValue = parseFloat(value);

      if (value < minValue && value.length !== 0) setValue((prevState) => prevState);
      else setValue((prevState) => (floatValue <= maxValue ? floatValue : prevState));
    }
  }

  return (
    <Box display="flex" flexDirection="column" flexWrap="nowrap" width="100%">
      <TextField
        fullWidth
        type={type}
        placeholder={placeholder}
        variant="outlined"
        value={value}
        onChange={(e) => handleChangeQuantity(e.target.value)}
        data-augmented-ui="border"
        InputProps={{
          inputProps: { max: maxValue, step: "0.01" }
        }}
        sx={InputSx}
        style={getAugmentedStyle({
          color: "#323232"
        })}
      />
      <Box
        mt={0.5}
        display="flex"
        flexWrap="nowrap"
        flexDirection={isMobile ? "column" : "row"}
        justifyContent="space-between">
        <Typography color="rgba(255, 255, 255, 0.60)" fontSize={12} fontWeight={400}>
          {helperText}
        </Typography>
        {type === "number" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              fontSize: "16px",
              gap: "8px",
              position: "relative",
              top: isMobile ? "-60px" : "-40px",
              right: isMobile ? "-72%" : "16px"
            }}>
            AGI
            <Typography
              color="primary"
              variant="caption"
              onClick={() => handleChangeQuantity(maxValue)}
              sx={{
                ml: "8px",
                cursor: "pointer",
                fontFamily: "Canva Sans Sans",
                color: "#FF8736",
                fontSize: "16px"
              }}>
              MAX
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

function CustomEmailField({ value, setValue, placeholder }) {
  const EmailInputSx = {
    ...InputSx,
    input: { ...InputTextSx, fontWeight: 400 }
  };

  return (
    <Box display="flex" flexDirection="column" flexWrap="nowrap" width="100%">
      <TextField
        fullWidth
        type="email"
        placeholder={placeholder}
        variant="outlined"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        data-augmented-ui="tl-clip br-clip border"
        sx={EmailInputSx}
        style={getAugmentedStyle({ color: "grey", borderSize: "1.5px" })}
      />
    </Box>
  );
}

function TransferConfirmDialog({ open, setClose, confirmHandler, email, amount }) {
  return (
    <ConfirmationDialog open={open} width="470px" closeDialog={setClose}>
      <Box>
        <Typography
          fontSize={{ xxs: 18, sm: 20 }}
          fontWeight={600}
          mb={{ xxs: 1.5, sm: 2.5 }}
          textAlign="center">
          Transaction Confirmation
        </Typography>
        <Typography fontSize={{ xxs: 14, sm: 16 }} textAlign="center">
          {toFixed(amount)} AGI will be transferred successfully
          {/* <Typography sx={{ color: (theme) => theme.palette.primary.main }}>{email}</Typography> */}
        </Typography>
        <Box display="flex" justifyContent="center" mt={{ xxs: 2.5, sm: 3 }} mb={1}>
          <OutlinedButton
            label="Cancel"
            classname="augmented-button-primary"
            color="primary"
            handleClick={setClose}
            sx={{
              width: { xxs: 83, sm: 104 },
              height: { xxs: 28, sm: 30 },
              mr: { xxs: 2.5, sm: 3.75 },
              fontSize: { xxs: 12, sm: 14 }
            }}
          />
          <CustomButton
            augmented="tl-clip br-clip"
            sx={{
              mt: 0,
              width: { xxs: 83, sm: 104 },
              height: { xxs: 28, sm: 30 },
              fontSize: 14
            }}
            handleClick={confirmHandler}>
            Confirm
          </CustomButton>
        </Box>
      </Box>
    </ConfirmationDialog>
  );
}

export function TransferSuccessDialog() {
  const dispatch = useDispatch();
  const { push } = useHistory();
  const { transferSuccessDialogOpen, transferSuccessResult: transfer } = useSelector(
    (state) => state.DMA
  );
  const setClose = () => dispatch(setTransferSuccessDialogOpen(false));

  const handleNavigateClick = () => {
    setClose();
    setTimeout(() => {
      dispatch(getUserInfo());
    }, 3000);
    // push(PATH_HISTORY.transfer);
  };

  if (!transfer) {
    return;
  }

  const labelSx = {
    color: (theme) => theme.palette.grey[50],
    display: "inline-block",
    width: 84,
    mt: "12px"
  };

  return (
    <CustomDialog
      hasCloseButton={true}
      BackdropProps={{
        sx: {
          backgroundColor: (theme) => alpha(theme.palette.background.default, 0.7)
        }
      }}
      PaperProps={{
        sx: {
          width: {
            xxs: "340px",
            sm: "500px"
          }
        }
      }}
      setClose={setClose}
      open={transferSuccessDialogOpen}>
      <Box sx={{ pt: { xxs: 0, sm: 2 }, px: { xxs: 0, sm: 4.5 } }}>
        <Typography
          fontSize={{ xxs: 18, sm: 20 }}
          fontWeight={600}
          align="center"
          mb={{ xxs: 3, sm: 5 }}>
          Transaction Completed
        </Typography>
        <Typography
          fontSize={{ xxs: 26, sm: 28 }}
          fontWeight={600}
          align="center"
          lineHeight={{ xxs: "31px", sm: "36.4px" }}
          sx={{ color: (theme) => theme.palette.primary.main }}
          mb={{ xxs: 1, sm: 5 }}>
          {toFixed(transfer.Amount)} {currencies.native}
        </Typography>
        <Box sx={{ mt: { xxs: 1, sm: 4 }, mb: 1.5, fontSize: { xxs: 12, sm: 14, lg: 16 } }}>
          {/* <Typography>
            <Typography sx={labelSx}>Recipient</Typography>
            {transfer.email}
          </Typography> */}
          <Typography>
            <Typography sx={labelSx}>Date</Typography>
            {formatDate(new Date(transfer.CreateTime).getTime() / 1000)}
          </Typography>
        </Box>
        <Box
          sx={{ display: "flex", flexFlow: "column nowrap", alignItems: "center", width: "100%" }}
          mt={6}
          mb={2}>
          <Box display="flex" flexWrap="nowrap" justifyContent="center" width="100%">
            <OutlinedButton
              size="medium"
              sx={{ fontSize: { xxs: 12, sm: 16 }, width: "50%", height: "37px", mr: "20px" }}
              handleClick={handleNavigateClick}
              label="Confirm"
              color="primary"
            />
            {/* <CustomButton
              handleClick={handleNavigateClick}
              sx={{ fontSize: { xxs: 12, sm: 16 }, height: "37px", width: "50%", mt: 0 }}
              augmented="tl-clip br-clip">
              Check Transaction
            </CustomButton> */}
          </Box>
        </Box>
      </Box>
    </CustomDialog>
  );
}

function TransferDialog({ open, setClose }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { balance: delysiumBalance, transferProcessing } = useSelector((state) => state.DMA);
  const { userInfo } = useSelector((state) => state.auth);
  const [amount, setAmount] = useState("");
  const [isShowConfirm, setIsShowConfirm] = useState(false);
  const [isShowBind2FAConfirm, setIsShowBind2FAConfirm] = useState(false);
  const [isShowBindEmailConfirm, setIsShowBindEmailConfirm] = useState(false);
  const [isShowVerifyDialog, setIsShowVerifyDialog] = useState(false);
  const [email, setEmail] = useState("");
  const [selectedCurrency] = useState(currencies.native);
  const downXs = useBreakpoints("down", "xs");
  // const selectedBalance = getBalance(balance, selectedCurrency);
  const [transferType, setTransferType] = useState("deposit");
  const [isSwapped, setIsSwapped] = useState(false);
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const balance = useMemo(() => {
    if (isSwapped) {
      return userInfo?.balance_agi || 0;
    }
    return userInfo?.dely_balance_agi || 0;
  }, [isSwapped, userInfo?.balance_agi, userInfo?.dely_balance_agi]);

  const handleSwap = () => {
    setIsSwapped(!isSwapped);
    setAmount(0);
  };

  useEffect(() => {
    if (open) {
      setEmail("");
      setAmount("");
    }
  }, [open]);

  const isTransferDisabled = useMemo(() => {
    return amount <= 0 || amount > balance;
  }, [amount, balance]);

  async function handlePreTransferClick() {
    // if (email === user.email) {
    //   dispatch(
    //     setAccountNotification({
    //       message: "Cannot transfer to self."
    //     })
    //   );
    //   return;
    // }

    // ReactGA.event("try_dmatransferAGI_confirm");
    setIsShowConfirm(true);
  }

  async function handleTransfer(code) {
    // @Case: No email
    // if (!user.email) {
    //   setIsShowBindEmailConfirm(true);
    //   return;
    // }

    // 1039710
    // 49624.400878931133852266

    // @Case: No binding 2FA
    // if (!user.authenticator.bound) {
    //   setIsShowBind2FAConfirm(true);
    //   return;
    // }

    // // @Case: Bound 2FA & No verify code
    // if (user.authenticator.bound && user.authenticator.enabled && !code) {
    //   setIsShowVerifyDialog(true);
    //   return;
    // }

    setIsShowConfirm(false);

    // const exist = await dispatch(checkEmailValidation(email));

    if (transferProcessing) {
      return;
    }

    // if (!exist) {
    //   return dispatch(
    //     setNotification({
    //       title: "Transfer Failed",
    //       type: NOTIF_FAIL,
    //       description: `The email ${email} has not been registered before, please check again.`
    //     })
    //   );
    // }

    // @FIXME: Require uniform code type at the BE
    return dispatch(transfer({ amount, transfer_type: !isSwapped ? "deposit" : "withdraw" }));
    // return dispatch(transfer({ amount: "10", transfer_type: "withdraw" }));
    // return dispatch(transfer({ amount: "200", transfer_type: transferType }));
  }

  return (
    <CustomDialog
      open={open}
      hasCloseButton={true}
      setClose={setClose}
      closeButtonType="crossLine"
      PaperProps={{
        sx: {
          width: { xxs: "101%", sm: 710 },
          height: { xxs: "550px", xs: "initial" },
          padding: { xxs: 0, xs: "0 6px" },
          margin: { xxs: 0, xs: 2 },
          left: -2,
          bottom: -2,
          background: { xxs: "#181818", xs: "black" },

          "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
            display: "none"
          }
        },
        style: {
          position: downXs ? "absolute" : "relative",
          "--aug-tr1": "12px",
          "--aug-tr-inset2": "calc(var(--aug-tr1) * 14.5)"
        },
        "data-augmented-ui": downXs ? "tr-clip-x border" : "bl-clip-y br-clip border"
      }}>
      <Box sx={{ pt: { xs: 3 }, pl: { xs: 7 }, pr: { xs: 7 }, pb: { xs: 3 } }}>
        <Typography
          fontSize={16}
          fontWeight={400}
          sx={{ fontFamily: "Canva Sans Sans", letterSpacing: "1.28px" }}>
          TRANSFER
        </Typography>
        {/* <InfoRow title="Asset" info={`${currencies.native}`} />
        <Box mt={4} sx={RowContainerSx}>
          <InfoRow title="Recipient's Email" rowSx={{ mt: 0, fontWeight: 400 }} />
          <CustomEmailField placeholder="someone@example.com" value={email} setValue={setEmail} />
        </Box> */}
        <Box
          sx={{
            width: "100%",
            height: "96px",
            padding: "16px",
            backgroundColor: "black",
            border: "0.5px solid #323232",
            justifyContent: "space-between",
            alignItems: "center",
            display: "inline-flex",
            marginTop: "20px",
            userSelect: "none",
            fontFamily: "Canva Sans Sans"
          }}>
          <Box
            sx={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "start",
              gap: "14px",
              display: "inline-flex"
            }}>
            <Box
              sx={{
                justifyContent: "start",
                alignItems: "center",
                gap: 4,
                display: "inline-flex"
              }}>
              <Typography
                sx={{
                  width: "52px",
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "14px",
                  fontWeight: "400",
                  fontFamily: "Canva Sans Sans",
                  textTransform: "capitalize",
                  lineHeight: "25.20px",
                  letterSpacing: "0.1em"
                }}>
                From
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "400",
                  fontFamily: "Canva Sans Sans",
                  textTransform: "uppercase",
                  lineHeight: "28px",
                  letterSpacing: "0.1em"
                }}>
                {isSwapped ? "Agent balance" : "Account balance"}
              </Typography>
            </Box>
            <Box
              sx={{
                justifyContent: "start",
                alignItems: "center",
                gap: 4,
                display: "inline-flex"
              }}>
              <Typography
                sx={{
                  width: "52px",
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "14px",
                  fontWeight: "400",
                  fontFamily: "Canva Sans Sans",
                  textTransform: "capitalize",
                  lineHeight: "25.20px",
                  letterSpacing: "0.1em"
                }}>
                To
              </Typography>
              <Typography
                sx={{
                  color: "white",
                  fontSize: isMobile ? "14px" : "16px",
                  fontWeight: "400",
                  fontFamily: "Canva Sans Sans",
                  textTransform: "uppercase",
                  lineHeight: "28px",
                  letterSpacing: "0.1em"
                }}>
                {isSwapped ? "Account balance" : "Agent balance"}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              width: "64px",
              height: "64px",
              position: "relative",
              backgroundColor: "black",
              borderRadius: "999px",
              overflow: "hidden",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              transition: "all 0.3s",

              "&:hover": {
                backgroundColor: isMobile ? "transparent" : "#323232"
              }
            }}
            onClick={handleSwap}>
            <SwapIcon sx={{ width: "25.51px", height: "32.62px" }} />
          </Box>
        </Box>
        <Box mt={4} sx={{}}>
          <Typography
            fontSize={16}
            fontWeight={400}
            sx={{ fontFamily: "Canva Sans Sans", letterSpacing: "1.28px", mb: "20px" }}>
            AGI
          </Typography>
          <Box
            sx={{
              width: "100%",
              height: "50px",
              px: "16px",
              backgroundColor: "black",
              border: "0.5px solid #323232",
              justifyContent: "space-between",
              alignItems: "center",
              display: "inline-flex"
            }}>
            <Box sx={{ justifyContent: "start", alignItems: "center", gap: 2, display: "flex" }}>
              <Box
                sx={{
                  width: "26px",
                  height: "26px",
                  borderRadius: "50%",
                  border: "0.5px solid #323232"
                }}>
                <LogoIcon />
              </Box>
              <Typography
                sx={{
                  color: "rgba(255, 255, 255, 0.6)",
                  fontSize: "16px",
                  fontWeight: "400",
                  fontFamily: "Canva Sans Sans",
                  textTransform: "uppercase",
                  lineHeight: "28px",
                  letterSpacing: "0.1em"
                }}>
                AGI - SOLANA
              </Typography>
            </Box>
            <Box
              sx={{
                width: "24px",
                height: "24px",
                px: "3.75px",
                pt: "7.88px",
                pb: "7.75px",
                opacity: 0,
                justifyContent: "center",
                alignItems: "center",
                display: "flex",
                overflow: "hidden"
              }}
            />
          </Box>
        </Box>
        <Box mt={4} sx={{}}>
          <Typography
            fontSize={16}
            fontWeight={400}
            sx={{ fontFamily: "Canva Sans Sans", letterSpacing: "1.28px", mb: "20px" }}>
            AMOUNT
          </Typography>
          {/* <CustomInputField
            maxValue={selectedBalance?.amount}
            helperText={`${selectedBalance?.amount || 0} ${
              selectedBalance?.currency || ""
            } available`}
            value={amount}
            setValue={setAmount}
          /> */}
          <CustomInputField
            maxValue={balance}
            helperText={`Available Amount: ${displayNum(balance) || 0} AGI`}
            placeholder={"MIN 0.000001"}
            value={amount}
            setValue={setAmount}
          />
        </Box>

        <Box
          sx={{ display: "flex", flexFlow: "column nowrap", alignItems: "center", width: "100%" }}
          mt={4}>
          <Box display="flex" flexWrap="nowrap" justifyContent="flex-start">
            <OutlinedButton
              size="medium"
              sx={{
                fontSize: "16px",
                width: "116px",
                height: "37px",
                mr: 3,
                fontFamily: "Canva Sans Sans"
              }}
              handleClick={setClose}
              label="Cancel"
              color="primary"
              disabled={transferProcessing}
              needCircular={false}
            />
            <CustomButton
              isLoading={transferProcessing}
              disabled={isTransferDisabled}
              handleClick={() => handlePreTransferClick()}
              sx={{
                fontSize: "16px",
                width: "116px",
                height: "37px",
                mt: 0,
                fontFamily: "Canva Sans Sans"
              }}
              augmented="tl-clip br-clip">
              Transfer
            </CustomButton>
          </Box>
        </Box>
      </Box>
      <BindConfirmDialog
        open={isShowBindEmailConfirm}
        setClose={() => setIsShowBindEmailConfirm(false)}
        tips={
          <>
            For your account safety, <br />
            please bind with&nbsp;
            <Box component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
              Email
            </Box>{" "}
            first.
          </>
        }
        confirmHandler={() => {
          ReactGA.event("try_dmatransferAGI_email_bind");
          dispatch(setCurrentFlow("emailUpdate"));
          dispatch(setCurrentDialogStep("emailInput"));
          setIsShowBindEmailConfirm(false);
        }}
      />
      <BindConfirmDialog
        open={isShowBind2FAConfirm}
        setClose={() => setIsShowBind2FAConfirm(false)}
        tips={
          <>
            For your account safety, <br />
            please bind with&nbsp;
            <Box component="span" sx={{ color: (theme) => theme.palette.primary.main }}>
              Google Authenticator.
            </Box>
          </>
        }
        confirmHandler={() => {
          ReactGA.event("try_dmatransferAGI_2FA_bind");
          dispatch(setBindGoogle2FADialogOpen(true));
          dispatch(setBindGoogle2FACallback("transfer"));
          setIsShowBind2FAConfirm(false);
        }}
      />
      <TransferConfirmDialog
        email={email}
        amount={amount}
        open={isShowConfirm}
        setClose={() => setIsShowConfirm(false)}
        confirmHandler={() => handleTransfer()}
      />
      <Google2FAVerifyDialog
        title={"Security Verification"}
        open={isShowVerifyDialog}
        setClose={() => {
          setIsShowVerifyDialog(false);
        }}
        onPrimaryClick={(code) => {
          return handleTransfer(code);
        }}
      />
    </CustomDialog>
  );
}

export default TransferDialog;
